import { format, isValid } from 'date-fns';
import { ar, fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { Loader2, History, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ReceiptEditHistory } from '@/types/entities';

interface EditHistoryPanelProps {
  entries: ReceiptEditHistory[];
  isLoading?: boolean;
  type: 'receipt';
}

export function EditHistoryPanel({ entries, isLoading, type }: EditHistoryPanelProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? ar : fr;
  const isRTL = i18n.language === 'ar';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-sm text-muted-foreground p-4 text-center">
        {t('inventory.editHistory.noChanges')}
      </div>
    );
  }

  // Format value based on field type
  const formatValue = (field: string, value: string): string => {
    if (field === 'date') {
      const date = new Date(value);
      if (isValid(date)) {
        return format(date, 'dd/MM/yyyy', { locale });
      }
      return value;
    }
    if (field === 'cost' || field === 'quantity') {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return field === 'cost' ? `${num.toFixed(2)} ${t('common.currency')}` : num.toString();
      }
    }
    return value;
  };

  // Get field translation key
  const getFieldLabel = (field: string): string => {
    return t(`inventory.editHistory.fields.${field}`, field);
  };

  // Group entries by timestamp (same edit session)
  const groupedByTime = entries.reduce((acc, entry) => {
    const timeKey = entry.editedAt;
    if (!acc[timeKey]) {
      acc[timeKey] = [];
    }
    acc[timeKey].push(entry);
    return acc;
  }, {} as Record<string, ReceiptEditHistory[]>);

  return (
    <div className="bg-muted/30 rounded-md p-3 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <History className="h-4 w-4" />
        {t('inventory.editHistory.title')}
      </div>

      <div className="space-y-2">
        {Object.entries(groupedByTime).map(([timestamp, changes]) => {
          const date = new Date(timestamp);
          const formattedDate = isValid(date)
            ? format(date, 'dd/MM/yyyy HH:mm', { locale })
            : timestamp;

          const modifiedBy = changes[0]?.modifiedBy;

          return (
            <div key={timestamp} className="bg-background rounded p-2 text-sm">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs text-muted-foreground">
                  {formattedDate}
                </div>
                {modifiedBy && (
                  <div className="text-xs text-muted-foreground italic">
                    {modifiedBy}
                  </div>
                )}
              </div>
              <div className="space-y-1">
                {changes.map((change) => {
                  // Special handling for deletion entries
                  if (change.fieldChanged === 'deleted') {
                    return (
                      <div key={change.id} className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-destructive">
                          {getFieldLabel(change.fieldChanged)}
                        </span>
                      </div>
                    );
                  }

                  // Regular field changes
                  return (
                    <div key={change.id} className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground/80">
                        {getFieldLabel(change.fieldChanged)}:
                      </span>
                      <span className="text-destructive/80 line-through">
                        {formatValue(change.fieldChanged, change.oldValue)}
                      </span>
                      <ArrowRight className={cn("h-3 w-3 text-muted-foreground", isRTL && "rotate-180")} />
                      <span className="text-green-600 dark:text-green-500 font-medium">
                        {formatValue(change.fieldChanged, change.newValue)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
